import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { MutatorTx, Schema, Tag } from "../schema"
import type { CreateValue } from "../types"
import type { DeleteID, UpdateValue } from "@rocicorp/zero"

export const TAG_NOTE_MAX_LENGTH = 2000

export const tagMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateValue<Tag>
			ctx: ZeroJwtPayload
		}) => {
			await tx.mutate.tags.insert({
				id: args.id,
				name: args.name,
				color: args.color,
				icon: args.icon,
				tagGroupId: args.tagGroupId,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
			})
		},
	),
	update: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateValue<Schema["tables"]["tags"]>
		}) => {
			await tx.mutate.tags.update({
				id: args.id,
				name: args.name,
				color: args.color,
				icon: args.icon,
				tagGroupId: args.tagGroupId,
			})
		},
	),
	// The teammate note reuses summaryString, the column the retired AI summary filled.
	updateNote: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { id: string; note: string }
			ctx: ZeroJwtPayload
		}) => {
			const [tag] = await tx.query.tags
				.where("id", "=", args.id)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			if (!tag) {
				throw new Error("Tag not found")
			}

			await tx.mutate.tags.update({
				id: args.id,
				summaryString: args.note.slice(0, TAG_NOTE_MAX_LENGTH),
				updatedAt: Date.now(),
			})
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["tags"]>
		}) => {
			await tx.mutate.tags.update({
				id: args.id,
				isDeleted: true,
			})

			// Remove tag from all feedbacks
			const feedbacksWithTag = await tx.query.feedbacks
				.whereExists("tags", (t) => t.where("id", args.id))
				.run()

			await Promise.all(
				feedbacksWithTag.map((feedback) =>
					tx.mutate.feedbackToTags.delete({
						A: feedback.id,
						B: args.id,
					}),
				),
			)
		},
	),
	linkTag: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { threadId: string; tagId: string }
			ctx: ZeroJwtPayload
		}) => {
			// Validate thread exists
			const [thread] = await tx.query.feedbacks
				.where("id", "=", args.threadId)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			if (!thread) {
				throw new Error("Thread not found")
			}

			// Validate tag exists
			const [tag] = await tx.query.tags
				.where("id", "=", args.tagId)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			if (!tag) {
				throw new Error("Tag not found")
			}

			// Check if relationship already exists
			const [existingLink] = await tx.query.feedbackToTags
				.where("A", "=", args.threadId)
				.where("B", "=", args.tagId)
				.run()

			if (existingLink) {
				// Find and delete the relationship
				const relationships = await tx.query.feedbackToTags
					.where("A", "=", args.threadId)
					.where("B", "=", args.tagId)
					.run()

				for (const relationship of relationships) {
					await tx.mutate.feedbackToTags.delete({
						A: relationship.A,
						B: relationship.B,
					})
				}
				return
			}

			// Create the relationship
			await tx.mutate.feedbackToTags.insert({
				A: args.threadId,
				B: args.tagId,
			})
		},
	),
	unlinkTag: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: { threadId: string; tagId: string }
			ctx: ZeroJwtPayload
		}) => {
			// Validate thread exists
			const [thread] = await tx.query.feedbacks
				.where("id", "=", args.threadId)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			if (!thread) {
				throw new Error("Thread not found")
			}

			// Validate tag exists
			const [tag] = await tx.query.tags
				.where("id", "=", args.tagId)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			if (!tag) {
				throw new Error("Tag not found")
			}

			// Find and delete all relationships between thread and tag
			const relationships = await tx.query.feedbackToTags
				.where("A", "=", args.threadId)
				.where("B", "=", args.tagId)
				.run()

			for (const relationship of relationships) {
				await tx.mutate.feedbackToTags.delete({
					A: relationship.A,
					B: relationship.B,
				})
			}
		},
	),
}
